import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { getGroupBySlug } from '../../../store/actions/group';
import ReactPixel, { AdvancedMatching } from "react-facebook-pixel";
import { captureException } from '../../../services/sentry';
import { DiscountDto, Group, PlanDto, Subscription, User } from '../../../types';
import Success from '../components/CheckoutPaymentSuccess';
import ErrorPayment from '../components/CheckoutPaymentError';
import ErrorSubscription from '../components/CheckoutSubscriptionError';
import { getUrlParamByName } from '../../../helpers/get-url-param';
import { getFreeSubscriptions } from '../../../helpers/getFreeSubscriptions';
import { isSubscribed } from '../../../helpers/isSubscribed';
import PurchaseDetails from './components/PurchaseDetails';
import { CheckoutContainer, Column } from './styles'
import { hideModal, showModal } from '../../../store/actions/modal';
import ProcessSubscriptionModal from './components/ProcessSubscriptionModal';
import action from "../../../helpers/rest-client";
import MercadoPagoForm from '../components/MercadoPagoForm';
import MpOwnForm from '../components/MpOwnForm';
import { getPlan } from '../../../store/actions/plan';
import { hybridEncryptData } from '../../../helpers/card-encrypter';
import { getSubscription } from '../../../store/actions/subscription-admin';
import { P } from '../../components/SignInForm/styles';

interface ComponentProps {
  isLoggedIn: boolean
  userSubscriptions: Subscription[]
  userSubscriptionStates: {
    loading: boolean
    success: boolean
    error: string | boolean
  }
  group: Group,
  plan: PlanDto,
  user: User,
  subscription: Subscription
  existDiscountStates: {
    loading: boolean,
    success: boolean
    error: string | boolean
  }
  existDiscountData: DiscountDto
}

const testGroups = [
  2850, 4117, 5306, 2201, 6964, 3354, 11185, 5979, 5776
]

const Component = ({
  isLoggedIn,
  userSubscriptions,
  userSubscriptionStates,
  plan,
  group,
  user,
  subscription,
}: ComponentProps) => {
  const dispatch = useDispatch();
  const [coupon, setCoupon] = useState<DiscountDto>()
  const [gatewayError, setGatewayError] = useState(false)
  const [appError, setAppError] = useState(false)
  const [success, setSuccess] = useState(false)

  const subscriptionId = useMemo(() => getUrlParamByName('subscriptionId'), []);
  const groupSlug = useMemo(() => getUrlParamByName('groupSlug'), [])
  const planId = useMemo(() => getUrlParamByName('planId'), [])
  const changePlan = useMemo(() => getUrlParamByName('changePlan'), [])

  const isUserSubscribed = useMemo(() => {
    if (isLoggedIn) {
      return isSubscribed(userSubscriptions, group?.id)
    } else if (!isLoggedIn || group?.group_type === 'free_experience') {
      return isSubscribed(getFreeSubscriptions(), group?.id)
    } else {
      return false
    }
  }, [userSubscriptions, group, isLoggedIn])


  const price = useMemo(() => {
    if (!!plan) {
      const selectedPlanDiscount = plan?.discounts?.length > 0 ? plan?.discounts[0] : null
      return (plan?.price || 0) - (selectedPlanDiscount?.amount || 0);
    }

    return 0
  }, [plan])

  const publicKey = useMemo(() => {
    if (!!plan) {
      return plan?.gateway?.credentials?.public?.public_key;
    }

    return 0
  }, [plan])

  const discount = useMemo(() => {
    if (!!plan) {
      const selectedPlanDiscount = plan?.discounts?.length > 0 ? plan?.discounts[0] : []
      return selectedPlanDiscount && [selectedPlanDiscount]
    }

    return []
  }, [plan])

  // const onSubmitOne = useCallback(async (cardData) => {
  //   dispatch(showModal('modal-process-new-subscription'))

  //   const payload = {
  //     plan_id: planId,
  //     discounts: discount,
  //     payment_details: cardData,
  //     idempotencyKey: `${user?.id}-${planId}`,
  //     ...(subscriptionId ? { reactivated_from_subscription_id: subscriptionId } : {})
  //   }

  //   action.Post({
  //     url: "/api/subscription/payment",
  //     body: payload,
  //   }).then((response: Subscription) => {
  //     onSuccess(response)
  //   }).catch((error) => {
  //     onError(error)
  //   }).finally(() => {
  //     dispatch(hideModal())
  //   });
  // }, [discount, price, planId]);


  const trackEvent = (subscription) => {
    if (!!subscription) {
      ReactPixel.track('Purchase', {
        value: subscription?.current_amount,
        currency: subscription?.plan?.currency,
        content_ids: [String(subscription?.group?.id)],
        content_type: 'product',
        contents: [{ quantity: 1, id: String(subscription?.group?.id) }],
        num_items: 1,
        order_id: subscription?.reference_id,
        // period: subscription?.plan?.period, // No sera tomado en cuenta por pixel
        // period_unit: subscription?.plan.period_unit, // No sera tomado en cuenta por pixel
        // price: subscription?.plan?.price, // No sera tomado en cuenta por pixel
        // subscriber_email: subscription?.subscriber?.email // No sera tomado en cuenta por pixel
      })

      console.log("[Pixel] Purchase enviado con:", {
        value: subscription?.current_amount,
        currency: subscription?.plan?.currency,
        content_ids: [String(subscription?.group?.id)],
        content_type: 'product',
        contents: [{ quantity: 1, id: String(subscription?.group?.id) }],
        num_items: 1,
        order_id: subscription?.reference_id,
        // period: subscription?.plan?.period, // No sera tomado en cuenta por pixel
        // period_unit: subscription?.plan.period_unit, // No sera tomado en cuenta por pixel
        // price: subscription?.plan?.price, // No sera tomado en cuenta por pixel
        // subscriber_email: subscription?.subscriber?.email // No sera tomado en cuenta por pixel
      });
    }
  }


  const onSuccess = (subscription) => {
    setSuccess(true)
    setGatewayError(false)
    setAppError(false)
    trackEvent(subscription)
  }

  const onError = (error) => {
    const errorSource = error?.response?.data?.source;
    const errorMessage = error?.response?.data?.message || error?.message;

    // Capturar en Sentry
    captureException(error, {
      operation: 'checkout_payment',
      feature: 'checkout',
      errorSource,
      errorMessage,
      planId,
      groupSlug,
      subscriptionId,
      userId: user?.id,
      price,
      tags: {
        feature: 'checkout',
        payment_gateway: plan?.gateway?.name || 'unknown'
      }
    });

    if (errorSource === 'gateway') {
      setGatewayError(true)
    } else if (errorSource === 'application') {
      setAppError(true)
    } else {
      setAppError(true)
    }
    setSuccess(false)
  }


  const onSubmitTwo = useCallback(async (cardData) => {
    dispatch(showModal('modal-process-new-subscription'))

    const encryptedCardData = await hybridEncryptData(JSON.stringify(cardData)).catch((error) => {
      console.error("Encryption failed:", error);

      // Capturar error de encriptación en Sentry
      captureException(error, {
        operation: 'checkout_card_encryption',
        feature: 'checkout',
        planId,
        groupSlug,
        userId: user?.id,
        tags: {
          feature: 'checkout',
          error_type: 'encryption_failed'
        }
      });

      throw new Error("Failed to encrypt card data");
    });

    const payload = {
      plan_id: planId,
      discounts: [
        ...discount,
        ...(!!coupon ? [coupon] : [])
      ],
      payment_details: {
        card_data: encryptedCardData,
        payer: {
          ...(process.env.REACT_APP_ENVIRONMENT !== "production" ? { email: "test_user_1799191595@testuser.com" } : { email: user?.email }),
          // email: user?.email
        }
      },
      idempotencyKey: `${user?.id}-${planId}`,
      ...(subscriptionId ? { reactivated_from_subscription_id: subscriptionId } : {})
    }

    action.Post({
      url: "/api/subscription/payment",
      body: payload,
    }).then((response: Subscription) => {
      onSuccess(response)
    }).catch((error) => {
      console.error("API call failed:", error);
      onError(error)
    }).finally(() => {
      dispatch(hideModal())
    });
  }, [coupon, discount, price, planId, user]);


  useEffect(() => {
    if (!!subscriptionId && (!subscription || (!!subscriptionId && subscription?.id !== Number(subscriptionId)))) {
      dispatch(getSubscription(Number(subscriptionId)));
    }
  }, [subscriptionId]);

  useEffect(() => {
    if (!!groupSlug && (!group || (!!group && group?.slug !== groupSlug))) {
      dispatch(getGroupBySlug(groupSlug));
    }
  }, [groupSlug]);

  useEffect(() => {
    if (!!planId && (!plan || (!!plan && plan?.id !== Number(planId)))) {
      dispatch(getPlan({ idPlan: Number(planId) }));
    }
  }, [planId]);

  useEffect(() => {
    if (!subscription || !plan || !group) return;

    const createdAt = new Date(Number(subscription.started_at) * 1000);
    const now = new Date();
    const ageInDays = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24);

    if (ageInDays > 30 && subscription?.status === "not_paid") {
      if (plan.status === "active") {
        window.location.href = `/checkout/payment?groupSlug=${group.slug}&planId=${plan.id}`;
      } else {
        const otherPlanWithSameFrequency = group.plans.find((p) => p.status === "active" && p.period === plan.period && p.period_unit === plan.period_unit && p.currency === plan.currency && p.id !== plan.id)
        if (!!otherPlanWithSameFrequency) {
          window.location.href = `/checkout/payment?groupSlug=${group.slug}&planId=${otherPlanWithSameFrequency.id}`;
        } else {
          window.location.href = `/group/${group.slug}?scrollTo=price`;
        }
      }
    }
  }, [subscription, plan, group]);

  useEffect(() => {
    if (subscription && isUserSubscribed && changePlan !== "true") {
      const createdAt = new Date(Number(subscription?.started_at) * 1000);
      const now = new Date();
      const ageInDays = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24);

      // Si el usuario ya está suscrito y no está cambiando de plan, redirigirlo a la librería
      // Excepción: si la suscripción no está pagada y tiene más de 30 días, mantenerlo en esta página
      if (!(subscription?.status === "not_paid" && ageInDays > 30)) {
        window.location.href = `/group/${groupSlug}/library`;
        return;
      }

    }
  }, [isUserSubscribed, subscription, groupSlug])

  useEffect(() => {
    if (!planId || !groupSlug) {
      const referrer = document.referrer;
      if (referrer) {
        window.location.href = referrer;
      } else if (groupSlug) {
        window.location.href = `/group/${groupSlug}`;
      } else {
        window.location.href = `/404`;
      }
    }
  }, [planId, groupSlug])


  if (!isLoggedIn || !group || !plan || userSubscriptionStates.loading || plan?.group?.id !== group?.id || !user) {
    return <></>
  }

  //check the user is the owner of the subscription
  if (!!subscriptionId && (subscription?.id !== Number(subscriptionId) || subscription?.subscriber?.id !== user?.subscriber?.id)) {
    window.location.href = "/profile/manage-subscriptions"
  }

  return (
    <>
      <CheckoutContainer>
        {!gatewayError && !appError && !success && (
          <>
            <Column>
              <MpOwnForm
                onError={onError}
                onSuccess={onSuccess}
                onSubmit={onSubmitTwo}
                publicKey={publicKey || ""}
                title={"Datos de pago"}
                price={price}
                submitText="Suscribirme"
                user={user}
              />
              {/* ) : ( */}
              {/* <MercadoPagoForm
                  onError={onError}
                  onSuccess={onSuccess}
                  onSubmit={onSubmitOne}
                  publicKey={publicKey || ""}
                  title={"Datos de pago"}
                  price={price}
                  submitText="Suscribirme"
                  user={user}
                />
              )} */}
            </Column>
            <PurchaseDetails setCoupon={setCoupon} coupon={coupon} />
          </>
        )}
      </CheckoutContainer>

      <ProcessSubscriptionModal />

      {(!gatewayError && appError && !success) && <ErrorSubscription />}
      {(gatewayError && !appError && !success) && <ErrorPayment title={<>No pudimos procesar tu pago</>} text={<>Hola {user?.name}. No te preocupes, esto puede suceder frecuentemente con suscripciones. Alguno de los datos puede estar incorrectos, tu tarjeta es invalida o no es soportada para este tipo de pagos automáticos. Prueba reintentando con el mismo u otro método de pago.</>} />}
      {(!gatewayError && !appError && success) && <Success />}
    </>
  );
};

const state = ({ userStore, groupStore, planStore, subscriptionAdminStore, discountStore }) => {
  const { isLoggedIn } = userStore;
  const { data: userSubscriptions, states: userSubscriptionStates } = userStore.subscriptions;
  const { data: group } = groupStore?.group;
  const { data: plan } = planStore?.onePlan;
  const { data: user } = userStore.information;
  const { data: subscription } = subscriptionAdminStore.subscriptionAdmin;
  const { data: existDiscountData, states: existDiscountStates } = discountStore?.existDiscount;

  return {
    isLoggedIn,
    user,
    userSubscriptions,
    userSubscriptionStates,
    group,
    plan,
    subscription,
    existDiscountData,
    existDiscountStates
  };
};

export default connect(state)(Component);